<template>
    <MultiSelectFromDatasourceInput
        ref="multiSelectFromDatasourceInput"
        :dataSource="dataSource"
        :name="name"
        :keyProp="keyProp"
        :textProp="textProp"
        v-model="selectedValues"
        @onChange="onChange"
    />
</template>

<script setup lang="ts">
    import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
    import { logError } from "@/shared/logging/errors";
    import { onMounted, ref, watch } from "vue";

    import MultiSelectFromDatasourceInput from "./MultiSelectFromDatasourceInput.vue";

    const selectedValues = defineModel<string[] | undefined>({
        required: true,
    });

    const props = defineProps<{
        selectOptions: any[];
        keyProp?: string;
        textProp?: string;
        name?: string;
    }>();

    const emit = defineEmits<{
        onChange: [values: string[]];
    }>();

    const dataSource: IDataSource<any> = {
        getData: async () => {
            try {
                return {
                    limit: 0,
                    offset: 0,
                    hasNext: false,
                    hasPrev: false,
                    items: props.selectOptions,
                };
            } catch (ex) {
                logError(ex);
                throw ex;
            }
        },
    };

    const multiSelectFromDatasourceInput = ref<InstanceType<typeof MultiSelectFromDatasourceInput> | null>(null);

    async function refreshDataSource() {
        if (!multiSelectFromDatasourceInput.value) return;
        await multiSelectFromDatasourceInput.value.refreshDataSource();
    }

    function setError(hasError: boolean) {
        if (!multiSelectFromDatasourceInput.value) return;
        multiSelectFromDatasourceInput.value.setError(hasError);
    }

    function onChange(values: string[]) {
        emit("onChange", values);
    }

    onMounted(() => {
        refreshDataSource();
    });

    watch(
        () => props.selectOptions,
        () => {
            refreshDataSource();
        }
    );

    defineExpose({
        refreshDataSource,
        setError,
    });
</script>
